import React from 'react';
import { SandpackProvider } from '@codesandbox/sandpack-react';

import { transformValues } from '@/utils';
import FullWidthChild from '@/components/ContentGrids/FullWidthChild';

import { CodeEditorProvider } from './CodeEditorContext';
import { useDuplicateIdWarning } from './Sandbox.helpers';
import presets from './presets';
import type { FileContents } from './Sandbox.types';

// Instead of relying on CodeSandbox to always exist, I'm self-hosting
// the bundler code. It's deployed at a separate domain, using
// Vercel. Check it out on Github:
// https://github.com/joshwcomeau/sandpack-bundler
const BUNDLER_URL = 'https://sandpack-bundler.vercel.app/';

const ResetFilesContext = React.createContext<() => void>(() => {});

type PresetKeys = keyof typeof presets;

interface Props {
  id: string;
  size: 'normal' | 'wide';
  preset?: PresetKeys;
  files: Record<string, FileContents>;
  activePath?: string;
  dependencies?: Record<string, string>;
  showLineNumbers: boolean;
  children: React.ReactNode;
}

const SandboxProvider = ({
  id,
  size,
  preset = 'react',
  files: filesFromMdx,
  activePath,
  dependencies = {},
  showLineNumbers,
  children,
}: Props) => {
  const [files, setFiles] = React.useState(() =>
    prepFiles(preset, filesFromMdx)
  );
  const { template } = presets[preset];

  // NOTE: it's *very important* that `customSetup`
  // is memoized, otherwise Sandpack will re-mount
  // and clear out any code edits. Very confusing!
  const customSetup = React.useMemo(() => {
    const { template, files, ...presetData } = presets[preset];

    return {
      ...presetData,
      dependencies,
    };
  }, [preset, dependencies]);

  const resetFiles = React.useCallback(() => {
    const files = prepFiles(preset, filesFromMdx);

    setFiles(files);
  }, [preset, filesFromMdx]);

  useDuplicateIdWarning(id);

  // NOTE: We need to render *some* sort of DOM node wrapper, otherwise the grid breaks. But we only need custom CSS for wide sandboxes.
  const Wrapper = size === 'wide' ? FullWidthChild : 'div';

  return (
    <ResetFilesContext.Provider value={resetFiles}>
      <Wrapper>
        <SandpackProvider
          template={template}
          files={files}
          options={{
            id,
            activeFile: activePath,
            bundlerURL: BUNDLER_URL,
            recompileMode: 'delayed',
            recompileDelay: 500,
          }}
          customSetup={customSetup}
        >
          <CodeEditorProvider showLineNumbers={showLineNumbers}>
            {children}
          </CodeEditorProvider>
        </SandpackProvider>
      </Wrapper>
    </ResetFilesContext.Provider>
  );
};

function prepFiles(
  preset: PresetKeys,
  filesFromMdx: Record<string, FileContents>
) {
  let { files: presetFiles } = presets[preset];

  const allFiles = {
    ...presetFiles,
    ...filesFromMdx,
  };

  // For all code passed in `files`, we want to trim any leading
  // or trailing whitespace / newlines. This is because it's easier
  // to add newlines when writing code in MDX
  return transformValues(allFiles, (name, file) => {
    if (typeof file === 'string') {
      return file.trim();
    } else {
      return {
        ...file,
        code: file.code.trim(),
      };
    }
  });
}

export function useResetFiles() {
  const resetFiles = React.useContext(ResetFilesContext);

  if (resetFiles === undefined) {
    throw new Error('useResetFiles must be used within a Provider');
  }

  return resetFiles;
}

export default SandboxProvider;
